<template>
  <div
    class="MessageContainer"
    :class="{
      Pending: message.status === 'pending',
      Typing: message.is_typing,
      ThirdPart: message.third_party,
      Error: message.status === 'error',
      in: direction === 'in',
      out: direction === 'out',
      edit: !!message.edit,
      CanEdit: canEdit && message.status !== 'error',
      BiggerText: useUIStore().accessibility.biggerText === 1,
      BiggerText2: useUIStore().accessibility.biggerText === 2,
      BiggerText3: useUIStore().accessibility.biggerText === 3,
      HighContrast: useUIStore().accessibility.highContrast,
    }"
  >
    <div class="Message relative">
      <div
        v-if="!message.edit"
        class="MessageText"
      >
        <template v-if="showIsTypingIcon">
          <icon-is-typing />
        </template>
        <span
          v-else
          class="MessagePre"
          v-html="messageParsed"
        />
        <transition
          name="custom-classes"
          enter-from-class="opacity-0 scale-0"
          leave-active-class="opacity-0"
          class="transition-[opacity,transform] duration-200 origin-bottom-right opacity-75"
          :duration="200"
          mode="out-in"
        >
          <button
            v-show="props.lastReadBy.length && direction === 'out'"
            :title="lastReadByTitle"
            class="absolute bottom-1 right-1 opaXcity-80"
          >
            <icon-check-double
              class="w-3 h-auto"
            />
          </button>
        </transition>
      </div>
      <div
        v-else
        class="w-full"
      >
        <el-input
          ref="InputRef"
          v-model="editMessage"
          class="EditInput"
          type="textarea"
          autosize
        />
      </div>
    </div>
    <!-- is not delivered -->
    <div class="MessageThrobber opacity-100 px-4">
      <icon-el-loading
        v-if="message.status === 'pending'"
        class="animate-spin w-4 h-4 [animation-duration:1.5s]"
      />
      <el-tooltip
        v-if="message.status === 'error'"
        :placement="breakpointSmallerOrEqualLg ? 'top' : 'left'"
        :content="$t('ChatMessage.resendTitle')"
      >
        <icon-el-warning-filled
          class="text-danger-500 cursor-pointer w-4 h-4"
          @click="emit('resend-message', message)"
        />
      </el-tooltip>
    </div>
    <!-- is delivered -->
    <template v-if="canEdit && message.status !== 'error'">
      <div
        v-if="!message.edit"
        class="OnHover mx-2"
      >
        <el-button
          size="small"
          circle
          @click="emit('toggle-edit-message')"
        >
          <icon-edit />
        </el-button>
      </div>
      <div
        v-else
        class="flex flex-row-reverse"
      >
        <el-button
          class="mr-3"
          type="default"
          circle
          plain
          @click="emit('toggle-edit-message')"
        >
          <icon-close />
        </el-button>
        <el-button
          class="mr-3"
          type="success"
          circle
          @click="emit('edit-message', { ...message, content: editMessage })"
        >
          <icon-check />
        </el-button>
      </div>
    </template>
  </div>
</template>

<script setup>
const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  direction: {
    type: String,
    validator: (value) => ['in', 'out'].includes(value),
    required: true,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
  showIsTypingIcon: {
    type: Boolean,
    default: false,
  },
  lastReadBy: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['resend-message', 'toggle-edit-message', 'edit-message']);

// Refs
const editMessage = ref('');
const InputRef = ref(null);

// Computed
const breakpointSmallerOrEqualLg = useBreakpoint.smallerOrEqual('lg');
const messageParsed = computed(() => {
  const message = wrapUrl(props.message.content);

  if (useAuthStore().user?.is_operator) {
    return wrapPhone(message);
  }

  return message;
});
const lastReadByTitle = computed(() => {
  const readByUsers = (props.lastReadBy || []).map((user) => user.name || user.full_name || `${user.first_name} ${user.last_name}`).join(', ');
  return `Zobrazeno (${readByUsers})`;
});

// Methods
const wrapUrl = (message = '') => {
  if (!message.includes('//')) {
    return message;
  }

  const urlPattern = /(?:(?:https?|ftp):\/\/)?(?:\S+(?::\S*)?@)?(?:(?!10(?:\.\d{1,3}){3})(?!127(?:\.\d{1,3}){3})(?!169\.254(?:\.\d{1,3}){2})(?!192\.168(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\x{00a1}\-\x{ffff}0-9]+-?)*[a-z\x{00a1}\-\x{ffff}0-9]+)(?:\.(?:[a-z\x{00a1}\-\x{ffff}0-9]+-?)*[a-z\x{00a1}\-\x{ffff}0-9]+)*(?:\.(?:[a-z\x{00a1}\-\x{ffff}]{2,})))(?::\d{2,5})?(?:\/[^\s]*)?/ig;

  return message.replace(urlPattern, (url) => {
    const protocolPattern = /^(?:(?:https?|ftp):\/\/)/i;
    const href = protocolPattern.test(url) ? url : 'http://' + url;
    return `<a href="${href}" target="_blank">${url}</a>`;
  });
};

const wrapPhone = (message = '') => {
  return phoneLinks(message)
    .map(({ value, isPhone }) => (isPhone
      ? `<a href="javascript:window.openCallingBox('${value}')">${value}</a>`
      : value))
    .join('');
};

// Watch
watch(() => props.message.edit, (value) => {
  if (value) {
    editMessage.value = props.message.content;
    nextTick(() => {
      InputRef.value?.focus();
    });
  }
});
</script>

<style scoped>
@reference "assets/css/tailwind.css";

@keyframes typing-animation {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }

  100% {
    opacity: 1;
  }
}

.MessageContainer {
  display: flex;
  align-items: center;

  &.Pending,
  &.Error {
    .Message {
      opacity: 0.5;
    }
  }

  &.Typing {
    .Message {
      animation: typing-animation 2s;
      animation-iteration-count: infinite;
    }
  }

  &.CanEdit:hover {
    .MessageThrobber {
      display: none;
    }
  }

  &.edit {
    .MessageThrobber {
      display: none;
    }
  }

  &:not(:last-child) {
    margin-bottom: 2px;
  }

  .MessageThrobber {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .Message {
    display: flex;
    padding: 8px 16px;
    min-height: 44px;
    border-radius: 12px;
    align-items: center;

    .MessageText {
      padding: 0;

      svg {
        width: 30px;
        height: 20px;
        margin-top: 5px;
      }

      .MessagePre {
        overflow-wrap: anywhere;
        word-break: break-word; /* fallback for browsers without `overflow-wrap: anywhere` */
        white-space: pre-wrap;
      }
    }
  }

  &.in {
    flex-direction: row;

    .Message {
      @apply text-white;

      @apply bg-primary-500;
      border-radius: 2px 12px 12px 2px;

      :deep(a) {
        @apply text-white;
      }
    }

    &.HighContrast {
      .Message {
        @apply bg-primary-700;
      }
    }

    &:first-of-type {
      .Message {
        border-top-left-radius: 12px;
      }
    }

    &:last-of-type:not(:only-child) {
      .Message {
        border-bottom-left-radius: 12px;
      }
    }
  }

  &.out {
    flex-direction: row-reverse;

    &:not(.edit) {
      .Message {
        @apply text-dark;
        background-color: #f3f3f3;
        border-radius: 12px 2px 2px 12px;
      }

      &:first-of-type {
        .Message {
          border-top-right-radius: 12px;
        }
      }

      &:last-of-type:not(:only-child) {
        .Message {
          border-bottom-right-radius: 12px;
        }
      }

      &.HighContrast {
        .Message {
          @apply text-black;
          background-color: #f7f7f7;
        }
      }
    }

    &.edit {
      .Message {
        @apply border border-primary-500;
        border-radius: 2px;

        .EditInput {
          border: none;
          line-height: 24px;
          font-size: 16px;

          :deep(textarea) {
            box-shadow: none;
          }

          &:focus {
            outline: none;
          }
        }
      }
    }
  }

  &.ThirdPart {
    &:not(.edit) {
      .Message {
        @apply bg-info;
        @apply text-white;

        :deep(a) {
          @apply text-white;
        }
      }

      &.HighContrast {
        .Message {
          background-color: #003d9b; /* 30% darken color-info */
        }
      }
    }
  }

  &.BiggerText {
    .MessagePre {
      font-size: 19px;
    }
  }

  &.BiggerText2 {
    .MessagePre {
      font-size: 22px;
    }
  }

  &.BiggerText3 {
    .MessagePre {
      font-size: 30px;
    }
  }

  .OnHover {
    display: none;
  }

  &:hover .OnHover {
    display: block;
  }
}
</style>

<style>
@reference "assets/css/tailwind.css";

.edit {
  .Message {
    @apply border border-primary-500;
    border-radius: 2px;
    width: 100%;
    padding: 0 !important;

    .EditInput.el-textarea {
      height: 100%;
      display: flex;

      textarea {
        padding: 0 16px;
        flex: 1 0 auto;
        border: none;
        line-height: 24px;
        font-size: 16px;
        @apply rounded-xl;

        &:focus {
          outline: none;
        }
      }
    }
  }
}
</style>
